/**
 * Привязка стрелки комментария к объекту, когда тянут её конец.
 *
 * Пока конец стрелки тянут, объект под курсором подсвечен синим — к нему она прицепится.
 * Отпустили над стеной, проёмом, предметом, прибором или светильником — комментарий
 * привязан: стрелка дальше ездит за объектом, а комментарий живёт в его слое. Отпустили
 * над пустым местом — комментарий свободный, стрелка указывает на точку плана.
 * Сам конец стрелки двигает общий жест перетаскивания (`beginCommentDrag`); здесь —
 * только что под курсором и привязка в конце жеста. Нажали на ручку и отпустили, не сдвинув, —
 * привязка не меняется.
 *
 * Пока тянут, стрелка рисуется по сохранённой точке (`draggingId`), а не по объекту:
 * иначе привязанный комментарий не дал бы оттянуть стрелку от своего объекта.
 *
 * Та же синяя подсветка — у инструмента «Коммент.» (`picking`): объект под курсором
 * показывает, к чему привяжется новый комментарий, если кликнуть.
 */
import { useRef, useState } from "react";
import type { Pt } from "@/shared/lib/geometry";
import { useProjectStore } from "@/shared/store";
import { useCommentsStore } from "@/shared/store/layers";
import { commentAnchor, commentTargetAt } from "@/widgets/comments";
import { commentTargetUnder } from "../logic";
import { ATTACH_TINT_ID } from "../ui/HoverTints";
import { useHoverTint } from "./useHoverTint";

export const useCommentAttach = (picking: boolean) => {
	const [draggingId, setDraggingId] = useState<string | null>(null);
	const startAnchor = useRef<Pt | null>(null);
	const tint = useHoverTint(ATTACH_TINT_ID, draggingId !== null || picking);

	/** Начали тянуть конец стрелки: стрелка отрывается от объекта и идёт за курсором. */
	const begin = (commentId: string) => {
		const comments = useCommentsStore.getState();
		const comment = comments.items[commentId];
		if (!comment) return;
		// Жест двигает сохранённую точку — ставим её туда, куда стрелка указывает сейчас
		const anchor = commentAnchor(comment, useProjectStore.getState().project.layers);
		comments.updateComment(commentId, { anchor });
		startAnchor.current = useCommentsStore.getState().items[commentId]?.anchor ?? anchor;
		setDraggingId(commentId);
	};

	const handlePointerMove = (clientX: number, clientY: number) => {
		if (draggingId === null && !picking) return;
		tint.setHovered(commentTargetUnder(clientX, clientY)?.element ?? null);
	};

	/** Отпустили: привязываем к объекту под курсором или оставляем свободным. */
	const handlePointerUp = (clientX: number, clientY: number) => {
		if (draggingId === null) return;
		const id = draggingId;
		setDraggingId(null);
		tint.setHovered(null);
		const comments = useCommentsStore.getState();
		const comment = comments.items[id];
		const start = startAnchor.current;
		startAnchor.current = null;
		if (!comment || !start) return;
		if (comment.anchor.x === start.x && comment.anchor.y === start.y) return;
		const hit = commentTargetUnder(clientX, clientY);
		const layers = useProjectStore.getState().project.layers;
		const target = hit ? commentTargetAt(layers, hit.kind, hit.id, comment.anchor) : null;
		comments.updateComment(id, { target: target ?? undefined });
	};

	/** Курсор ушёл с холста — подсветку снимаем */
	const clearHover = () => tint.setHovered(null);

	return { draggingId, begin, handlePointerMove, handlePointerUp, clearHover };
};
